import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ORIGIN = 'https://ldglobal.co';
const DEFAULT_OG_IMAGE = `${ORIGIN}/favicon.png`;
const SUFFIX = 'Land Drafters';

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    const tag = selector.startsWith('link') ? 'link' : 'meta';
    el = document.createElement(tag);
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null) el.removeAttribute(k);
    else el.setAttribute(k, v);
  }
  return el;
};

const clearManaged = (cls) => {
  document.head.querySelectorAll(`.${cls}`).forEach(el => el.remove());
};

export default function useSeo({ title, description, path, image, type = 'website', keywords, geoRegion, geoPlacename, hreflangs, jsonLd, lang } = {}) {
  const location = useLocation();
  const effectivePath = path || (location ? location.pathname : '/');

  useEffect(() => {
    const fullTitle = title
      ? (title.includes(SUFFIX) ? title : `${title} | ${SUFFIX}`)
      : `${SUFFIX} | Global Engineering & CAD Services`;
    document.title = fullTitle;
    document.documentElement.setAttribute('lang', lang || 'en');

    const url = `${ORIGIN}${effectivePath.startsWith('/') ? effectivePath : '/' + effectivePath}`;
    const ogImg = image || DEFAULT_OG_IMAGE;
    const desc = description || 'Land Drafters — Global Reach for All Your Needs. Engineering and CAD drafting consultancy serving 40+ countries.';

    upsertMeta('meta[name="description"]',         { name: 'description', content: desc });
    upsertMeta('meta[name="robots"]',              { name: 'robots', content: 'index, follow, max-snippet:-1, max-image-preview:large' });
    upsertMeta('link[rel="canonical"]',            { rel: 'canonical', href: url });
    upsertMeta('meta[property="og:title"]',        { property: 'og:title', content: fullTitle });
    upsertMeta('meta[property="og:description"]',  { property: 'og:description', content: desc });
    upsertMeta('meta[property="og:url"]',          { property: 'og:url', content: url });
    upsertMeta('meta[property="og:type"]',         { property: 'og:type', content: type });
    upsertMeta('meta[property="og:image"]',        { property: 'og:image', content: ogImg });
    upsertMeta('meta[property="og:site_name"]',    { property: 'og:site_name', content: 'Land Drafters' });
    upsertMeta('meta[name="twitter:card"]',        { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]',       { name: 'twitter:title', content: fullTitle });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: desc });
    upsertMeta('meta[name="twitter:image"]',       { name: 'twitter:image', content: ogImg });

    if (keywords && keywords.length) {
      upsertMeta('meta[name="keywords"]', { name: 'keywords', content: Array.isArray(keywords) ? keywords.join(', ') : keywords });
    } else {
      document.head.querySelector('meta[name="keywords"]')?.remove();
    }
    if (geoRegion) upsertMeta('meta[name="geo.region"]', { name: 'geo.region', content: geoRegion });
    else document.head.querySelector('meta[name="geo.region"]')?.remove();
    if (geoPlacename) upsertMeta('meta[name="geo.placename"]', { name: 'geo.placename', content: geoPlacename });
    else document.head.querySelector('meta[name="geo.placename"]')?.remove();

    clearManaged('seo-hreflang');
    (hreflangs || []).forEach(({ hreflang, href }) => {
      const el = document.createElement('link');
      el.rel = 'alternate';
      el.hreflang = hreflang;
      el.href = href.startsWith('http') ? href : `${ORIGIN}${href}`;
      el.className = 'seo-hreflang';
      document.head.appendChild(el);
    });

    clearManaged('seo-jsonld');
    const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    blocks.forEach(obj => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.className = 'seo-jsonld';
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });

    return () => {
      clearManaged('seo-hreflang');
      clearManaged('seo-jsonld');
      if (lang) document.documentElement.setAttribute('lang', 'en');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, effectivePath, image, type, lang, JSON.stringify(keywords), geoRegion, geoPlacename, JSON.stringify(hreflangs), JSON.stringify(jsonLd)]);
}

export const ORIGIN_URL = ORIGIN;

export const breadcrumbLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map(([name, path2], i) => ({
    '@type': 'ListItem', position: i + 1, name, item: `${ORIGIN}${path2}`
  }))
});

export const serviceLd = ({ name, description, path, areaServed }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name, description,
  url: `${ORIGIN}${path}`,
  provider: { '@type': 'ProfessionalService', name: 'Land Drafters', url: ORIGIN, telephone: '+1 786-673-2523' },
  ...(areaServed ? { areaServed } : {})
});

export const faqLd = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(([q, a]) => ({
    '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a }
  }))
});
